import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDisplayDateTime } from "@/lib/utils";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Car,
  Phone,
  Mail,
  FileCheck,
  Ban,
  Activity,
  DollarSign,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import { useDrivers, useDeleteDriver, useToggleDriverActiveStatus } from "@/api-services/hooks/useDriver";
import { useOnDutyDriverList } from "@/api-services/hooks/useDashboard";
import { DriverDTO, PagedData } from "@/api-services/hooks/adminTypes";
import DeleteDriverConfirmation from "@/components/drivers/DeleteDriverConfirmation";
import KycStatusBadge from "@/components/drivers/KycStatusBadge";
import StatusToggleConfirmation from "@/components/drivers/StatusToggleConfirmation";
import { formatGlobalCurrency } from "@/api-services/hooks/useSettings";

const DriversPage = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Initialize state from URL params
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get("search") || "");
  const getValidKycFilter = (val: string | null) => {
  const allowed = ["PENDING", "APPROVED", "REJECTED", "KEEP_IN_VIEW"];
  return allowed.includes((val || "").toUpperCase()) ? val!.toUpperCase() : "all";
};

const [kycFilter, setKycFilter] = useState(getValidKycFilter(searchParams.get("kyc")));

 // const [kycFilter, setKycFilter] = useState(searchParams.get("kyc") || "all");
  const [activeFilter, setActiveFilter] = useState(searchParams.get("active") || "all");
  const [onDutyFilter, setOnDutyFilter] = useState(searchParams.get("onduty") === "true");

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "0", 10));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get("pageSize") || "10", 10));

  // Debounce search term (same as Base Pricing: reset page when debounced value updates)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Update URL params when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm) params.search = searchTerm;
    if (kycFilter !== "all") params.kyc = kycFilter;
    if (activeFilter !== "all") params.active = activeFilter;
    if (onDutyFilter) params.onduty = "true";
    if (currentPage > 0) params.page = String(currentPage);
    if (itemsPerPage !== 10) params.pageSize = String(itemsPerPage);
    setSearchParams(params);
  }, [searchTerm, kycFilter, activeFilter, onDutyFilter, currentPage, itemsPerPage, setSearchParams]);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<{ id: number; name: string; email?: string } | null>(null);

  // Status toggle confirmation state
  const [statusToggleOpen, setStatusToggleOpen] = useState(false);
  const [driverToToggle, setDriverToToggle] = useState<{ id: number; name: string; isActive: boolean } | null>(null);

  // Handle search input change - just update search term, no page reset
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Reset to first page when filters change (excluding debouncedSearchTerm - page resets when user stops typing)
  useEffect(() => {
    setCurrentPage(0);
  }, [kycFilter, activeFilter, onDutyFilter]);

  // Reset to first page when search changes (same as Base Pricing)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length >= 2;

  // Delete driver hook
  const { mutate: deleteDriver, isPending: isDeleting } = useDeleteDriver({
    onSuccess: async (data, variables) => {
      toast.success("Driver deleted successfully! Corresponding user account has also been removed.");
      // Invalidate all drivers queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error: any, variables) => {
      let errorMessage = "Failed to delete driver";
      if (error?.response?.status === 400) {
        errorMessage = "Cannot delete driver. Please check if driver has active rides.";
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to delete this driver.";
      } else if (error?.response?.status === 404) {
        errorMessage = "Driver not found. It may have already been deleted.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    },
  });

  // Toggle active status hook
  const { mutate: toggleActiveStatus, isPending: isTogglingStatus } = useToggleDriverActiveStatus({
    onSuccess: async (data, driverId) => {
      toast.success("Driver status updated successfully!");
      await queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error, driverId) => {
      toast.error(`Failed to update driver status: ${error.message}`);
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  // // Status is only toggleable when KYC is APPROVED; KEEP_IN_VIEW/REJECTED => show inactive
  // const isStatusToggleAllowed = (driver: DriverDTO) =>
  //   (driver.kycStatus || "").toUpperCase() === "APPROVED";
  const effectiveStatus = (driver: DriverDTO) => driver.isActive
        ? "active"
        : "inactive";

  // Helper function to handle driver status toggle
  const handleDriverStatusToggle = (driver: DriverDTO) => {
    if (!driver.driverId) {
      toast.error("Driver ID is missing. Cannot update status.");
      return;
    }
    // if (!isStatusToggleAllowed(driver)) {
    //   toast.error("Driver status can only be changed when KYC is Approved.");
    //   return;
    // }

    const displayName = getDriverDisplayName(driver);

    // Set driver to toggle and open confirmation dialog
    setDriverToToggle({
      id: driver.driverId,
      name: displayName,
      isActive: driver.isActive ?? true,
    });
    setStatusToggleOpen(true);
  };

  // Handle status toggle confirmation
  const handleStatusToggleConfirm = (driverId: number, remarks: string) => {
    // Call the API - list will refetch on success via invalidateQueries
    toggleActiveStatus(driverId);

    // Close the dialog
    setStatusToggleOpen(false);
    setDriverToToggle(null);
  };

  const handleStatusToggleCancel = () => {
    setStatusToggleOpen(false);
    setDriverToToggle(null);
  };

  // Fetch on-duty drivers
  // const { data: onDutyResponse } = useOnDutyDriverList({});
  // const onDutyDrivers: DriverDTO[] = (onDutyResponse as any)?.data || [];
  // const onDutyDriverIds = useMemo(() => new Set(onDutyDrivers.map((d: any) => d.driverId)), [onDutyDrivers]);

  // Fetch drivers from API with all filters
const queryParams = {
  page: currentPage,
  size: itemsPerPage,
  sort: ['driverId,desc'],

  ...(shouldSearch && { search: debouncedSearchTerm }),

  // ✅ KYC (IMPORTANT FIX)
  ...(kycFilter && kycFilter !== "all" && { kycStatus: kycFilter.toUpperCase() }),

  // ✅ STATUS → maps to filter in backend
  ...(onDutyFilter
    ? { status: "ONDUTY" }
    : activeFilter !== "all"
      ? { status: activeFilter === "active" ? "ACTIVE" : "INACTIVE" }
      : {}),
};

  const { data, isLoading: loading, error, refetch } = useDrivers(
    queryParams,
    {
      onError: (err) => {
        toast.error(`Failed to load drivers: ${err.message}`);
      },
    }
  );

const filteredDrivers = useMemo(() => {
  const driverData = data as PagedData<DriverDTO> | undefined;
  if (!driverData || !driverData.content) return [];
  return driverData.content;
}, [data]);

    // Auto-focus search input on page load (after loading completes)
    useEffect(() => {
      if (!loading && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [loading]);

  const getStatusBadge = (status: string) => {
    const statusLower = status?.toLowerCase() || "offline";
    const variants: Record<string, { variant: string; className: string }> = {
      onduty: {
        variant: "default",
        className: "bg-success text-success-foreground",
      },
      online: {
        variant: "default",
        className: "bg-success text-success-foreground",
      },
      active: {
        variant: "default",
        className: "bg-success text-success-foreground",
      },
      busy: {
        variant: "secondary",
        className: "bg-warning text-warning-foreground",
      },
      offline: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
      inactive: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
      suspended: {
        variant: "destructive",
        className: "bg-destructive text-destructive-foreground",
      },
      pending: {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
      },
    };
    return variants[statusLower] || variants.offline;
  };

  const handleDriverAction = (
    action: string,
    driverId: number,
    driverName: string
  ) => {
    if (action === "Edit Details") {
      navigate(`/admin/drivers/create?driverId=${driverId}`);
    } else {
      toast.success(`${action} action completed for ${driverName}`);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (driverId: number, driverName: string, driverEmail?: string) => {
    setDriverToDelete({ id: driverId, name: driverName, email: driverEmail });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!driverToDelete) return;

    deleteDriver({
      driverId: driverToDelete.id,
      email: driverToDelete.email,
    });
    setDeleteConfirmationOpen(false);
    setDriverToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setDriverToDelete(null);
  };

  // Use client-side filtered drivers
  const drivers = filteredDrivers;

  // Pagination calculations
  const driverData = data as PagedData<DriverDTO> | undefined;
 const totalItems = driverData?.page?.totalElements ?? driverData?.totalElements ?? 0;
const totalPages = driverData?.page?.totalPages ?? 0;

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Convert driver DTO to display format
  const getDriverDisplayName = (driver: DriverDTO) => {
    return driver.fullName || driver.email || "Unknown Driver";
  };

  const getDriverEmail = (driver: DriverDTO) => {
    return driver.email || "N/A";
  };

  const getDriverPhone = (driver: DriverDTO) => {
    if (!driver.phoneNumber) return "N/A";

    if (driver.phoneCountryCode) {
      return `${driver.phoneCountryCode} ${driver.phoneNumber}`;
    }
    
    return driver.phoneNumber;
  };

  const getDriverVehicle = (driver: DriverDTO) => {
    if (driver.vehicleMake && driver.vehicleModel && driver.vehicleYear) {
      return `${driver.vehicleMake} ${driver.vehicleModel} ${driver.vehicleYear}`;
    }
    return driver.vehicleType || "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading drivers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Drivers
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Driver Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and monitor your driver network
          </p>
        </div>
        {can('Create Drivers') && (
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate("/admin/drivers/create")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Driver
          </Button>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search drivers by name, email, phone, vehicle type,...(min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                autoComplete="off"
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <FileCheck className="w-4 h-4 mr-2" />
                  {kycFilter === "all"
                    ? "All KYC"
                    : kycFilter === "PENDING"
                      ? "Pending"
                      : kycFilter === "APPROVED"
                        ? "Approved"
                        : kycFilter === "REJECTED"
                          ? "Rejected"
                          : kycFilter === "KEEP_IN_VIEW"
                            ? "Keep in View"
                            : kycFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setKycFilter("all")}>
                  All KYC
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("PENDING")}>
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("APPROVED")}>
                  Approved
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("REJECTED")}>
                  Rejected
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setKycFilter("KEEP_IN_VIEW")}>
                  Keep in View
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[140px]">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {onDutyFilter
                    ? "On Duty"
                    : activeFilter === "all"
                      ? "All Status"
                      : activeFilter === "active"
                        ? "Active Only"
                        : "Inactive Only"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => { setActiveFilter("all"); setOnDutyFilter(false); }}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setActiveFilter("active"); setOnDutyFilter(false); }}>
                  Active Only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setActiveFilter("inactive"); setOnDutyFilter(false); }}>
                  Inactive Only
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setOnDutyFilter(true); setActiveFilter("all"); }}>
                  On Duty
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Drivers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Drivers ({totalItems})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="hidden sm:table-cell">Contact</TableHead>
                  <TableHead className="hidden md:table-cell">Vehicle Type</TableHead>
                  <TableHead>KYC Status</TableHead>
                  <TableHead className="hidden lg:table-cell">On Duty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Rating</TableHead>
                  <TableHead className="hidden lg:table-cell">Total Rides</TableHead>
                  <TableHead className="hidden lg:table-cell">Earnings</TableHead>
                  <TableHead className="hidden xl:table-cell">Created Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  drivers.map((driver) => {
                    const isOnDuty = driver.onDuty === true;
                    const status = effectiveStatus(driver);
                    const statusBadge = getStatusBadge(status);
                    // const canToggleStatus = isStatusToggleAllowed(driver);
                    const displayName = getDriverDisplayName(driver);
                    const initials = displayName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);
                    return (
                      <TableRow
                        key={driver.driverId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar>
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{displayName}</p>
                              <p className="text-sm text-muted-foreground">
                                ID: {driver.driverId}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm">
                              <Mail className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getDriverEmail(driver)}
                            </div>
                            <div className="flex items-center text-sm">
                              <Phone className="w-3 h-3 mr-1 text-muted-foreground" />
                              {getDriverPhone(driver)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center text-sm">
                            <Car className="w-3 h-3 mr-1 text-muted-foreground" />
                            {getDriverVehicle(driver)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <KycStatusBadge status={driver.kycStatus || "PENDING"} size="sm" />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {isOnDuty ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              On Duty
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {status.toLowerCase() === "online" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "active" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "busy" && (
                              <Clock className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "offline" && (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "inactive" && (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {status.toLowerCase() === "suspended" && (
                              <Ban className="w-3 h-3 mr-1" />
                            )}
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-chart-accent mr-1" />
                            {(driver.driverRating != null || driver.averageRating != null)
                              ? (driver.driverRating ?? driver.averageRating)!.toFixed(2)
                              : "N/A"}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex items-center text-sm">
                            <Activity className="w-4 h-4 text-muted-foreground mr-1" />
                            {driver.totalRides != null
                              ? Number(driver.totalRides).toLocaleString()
                              : "N/A"}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex items-center text-sm">
                            {/* <DollarSign className="w-4 h-4 text-muted-foreground mr-1" /> */}
                            {driver.totalEarnings != null
                              ? formatGlobalCurrency(driver.totalEarnings)
                              // Number(driver.totalEarnings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : "N/A"}
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell text-sm text-muted-foreground whitespace-nowrap">
                          {formatDisplayDateTime(driver.createdDate)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {can('Edit Drivers') && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDriverAction(
                                      "Edit Details",
                                      driver.driverId || 0,
                                      displayName
                                    )
                                  }
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                              )}
                              {can('Manage Driver KYC') && (
                                <DropdownMenuItem
                                  onClick={() => navigate(`/admin/drivers/${driver.driverId}?tab=kyc`)}
                                >
                                  <FileCheck className="w-4 h-4 mr-2" />
                                  KYC Review
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => navigate(`/admin/drivers/${driver.driverId}/ride-history`)}
                              >
                                <Car className="w-4 h-4 mr-2" />
                                Ride History
                              </DropdownMenuItem>
                              {can('Activate/Deactivate Drivers') && (
                                <DropdownMenuItem
                                  onClick={() => handleDriverStatusToggle(driver)}
                                  disabled={isTogglingStatus}
                                >
                                  {driver.isActive ? (
                                    <>
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {can('Delete Drivers') && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleDeleteClick(
                                        driver.driverId || 0,
                                        displayName,
                                        getDriverEmail(driver)
                                      )
                                    }
                                    className="text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Driver
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {drivers.length > 0 && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Driver Confirmation Modal */}
      <DeleteDriverConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        driverName={driverToDelete?.name || ""}
        driverId={driverToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />

      {/* Status Toggle Confirmation Modal */}
      <StatusToggleConfirmation
        open={statusToggleOpen}
        onOpenChange={handleStatusToggleCancel}
        driverName={driverToToggle?.name || ""}
        driverId={driverToToggle?.id || 0}
        isActive={driverToToggle?.isActive ?? true}
        onConfirm={handleStatusToggleConfirm}
        isLoading={isTogglingStatus}
      />
    </div>
  );
};

export default DriversPage;
